// @ts-nocheck
// @ts-ignore
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Navigate,
  useLocation,
  RouterProvider,
  createBrowserRouter,
  Outlet,
} from 'react-router-dom';
import { CssBaseline, IconButton, styled } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { SnackbarProvider, MaterialDesignContent } from 'notistack';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { loginSuccess } from './store/slices/UserSilcer';
import {
  showApplicationLauncher,
  extractAuthParams,
} from './utils/Utils';
import {
  isCommonAuthEnabled,
  validateCommonAuthParams,
} from './const/commonAuthConfig';
import useCurrentTheme from './Theme/useCurrentTheme';
import FullscreenLoader from './components/Common/FullScreenLoader';
import { initializeAxios } from './utils/Axios';
import { getProtectedRoutes, publicRoutes } from './Routes';
import './App.css';
import SuspenseComponent from './components/Common/SuspenseComponent';
import ErrorBoundary from './components/Common/ErrorBoundary';
import useThemeAppearance from './hooks/Configuration/useThemeAppearance';
import useAccountId from './hooks/Auth/useAccountId';
import useSSEConnect from './hooks/Configuration/useSSEConnect';
import AutomationReportToast from './components/Common/AutomationReportToast';
import WeaknessAssessmentToast from './components/Common/WeaknessAssessmentToast';
import AIAutofillProcessingToast from './components/Common/AIAutofill/AIAutofillProcessingToast';
import ExaminationReviewToast from './components/Common/ExaminationReviewToast';
import useCommonAuth from './hooks/Auth/useCommonAuth';
import { redirectToLogout, clearCommonAuthData } from './utils/commonAuthUtils';
import CommonAuthGuard from './components/Common/CommonAuthGuard';
import SessionExpiredModal from './components/Common/Modals/SessionExpiredModal';
import { identifyLoggedInUserWithUmami } from './utils/analytics/umami';
import {
  subscribeToSessionLogin,
  subscribeToSessionLogout,
} from './utils/sessionBroadcast';

import REGEX from './const/Regex';
import NotFound404 from './components/Common/NotFound404';
if (import.meta.env.MODE === 'development') {
  // import('./utils/axe-config');
}

const CloseSnackbarIconButton = ({ key, onClickSnackBarClose }) => {
  const onClick = useCallback(
    () => onClickSnackBarClose(key),
    [key, onClickSnackBarClose]
  );
  return (
    <IconButton onClick={onClick} title="Close">
      <CloseIcon
        sx={{
          color: (theme) => theme?.palette?.other?.white,
        }}
      />
    </IconButton>
  );
};

CloseSnackbarIconButton.propTypes = {
  onClickSnackBarClose: PropTypes.func.isRequired,
  key: PropTypes.string.isRequired,
};

const REALM_CONFIG = JSON.parse(localStorage.getItem('REALM_CONFIG'));

const isPathValid = ['/launcher', '/account'].includes(
  window.location.pathname
);

// Initialize authentication with priority: common auth > keycloak > custom
const initializeAuth = async () => {
  // Priority 1: Common Auth - Check for URL parameters first
  if (isCommonAuthEnabled()) {
    const urlParams = new URLSearchParams(window.location.search);
    const extractedParams = extractAuthParams(urlParams);
    if (validateCommonAuthParams(extractedParams)) {
      // Common auth will be handled in the App component
      // Initialize axios for common auth (same as custom auth)
      initializeAxios();
      return;
    }

    // Check if user already has common auth tokens stored
    if (localStorage.getItem('access_token')) {
      // User has existing common auth session, initialize axios
      initializeAxios();
      return;
    }
  }

  // Priority 3: Custom Auth - default authentication
  if (showApplicationLauncher() !== true || !isPathValid) {
    initializeAxios();
  }
};

// Initialize authentication
initializeAuth();

/**
 * Checks if common auth should proceed with loading
 * @param {boolean} hasCommonAuthParams - Whether common auth params exist
 * @param {boolean} loading - Loading state
 * @returns {boolean} True if should continue loading
 */
const shouldContinueCommonAuthLoading = (hasCommonAuthParams, loading) => {
  return isCommonAuthEnabled() && hasCommonAuthParams && loading;
};

/**
 * Checks if common auth should redirect to logout
 * @param {boolean} hasCommonAuthTokens - Whether common auth tokens exist
 * @param {boolean} hasCommonAuthParams - Whether common auth params exist
 * @returns {boolean} True if should redirect to logout
 */
const shouldRedirectToLogout = (hasCommonAuthTokens, hasCommonAuthParams) => {
  if (isCommonAuthEnabled() && hasCommonAuthTokens && !hasCommonAuthParams) {
    return true;
  }
  if (isCommonAuthEnabled() && !hasCommonAuthTokens && !hasCommonAuthParams) {
    return true;
  }
  return false;
};

/**
 * Handles common authentication logic and returns appropriate component or null
 * @param {URLSearchParams} params - URL search parameters
 * @param {boolean} loading - Loading state
 * @returns {JSX.Element|null} Component to render or null to continue
 */
const handleCommonAuth = (params, loading) => {
  const extractedParams = extractAuthParams(params);
  const hasCommonAuthParams = validateCommonAuthParams(extractedParams);
  const hasCommonAuthTokens =
    localStorage.getItem('access_token') && localStorage.getItem('REALM_ID');

  // If we have common auth URL parameters, let the useLayoutEffect handle the initialization
  // Don't redirect to logout while common auth is being processed
  if (shouldContinueCommonAuthLoading(hasCommonAuthParams, loading)) {
    // Common auth URL params detected and still loading, let it process
    return null; // Return null to indicate "continue processing"
  }

  if (shouldRedirectToLogout(hasCommonAuthTokens, hasCommonAuthParams)) {
    redirectToLogout();
    return null;
  }

  return undefined; // Return undefined to indicate "no action taken"
};

// eslint-disable-next-line react/prop-types
const PrivateRoute = ({ loading }) => {
  const { isAuthenticate } = useSelector((state) => state.users);
  const params = new URLSearchParams(window.location.search);
  const location = useLocation();

  useSSEConnect();

  if (!isAuthenticate) {
    // Priority 1: Handle common authentication
    const commonAuthResult = handleCommonAuth(params, loading);
    if (commonAuthResult !== undefined) {
      return commonAuthResult;
    }

    // Priority 2: Handle custom auth - redirect to login page
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <Outlet />;
};

const StyledMaterialDesignContent = styled(MaterialDesignContent)(
  ({ theme }) => ({
    '&.notistack-MuiContent': {
      width: 'fit-content',
      maxWidth: 400,
      display: 'flex',
      flexWrap: 'nowrap',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
    },
    '&.notistack-MuiContent-success': {
      backgroundColor: theme.palette.success.main,
    },
    '&.notistack-MuiContent-error': {
      backgroundColor: theme.palette.error.main,
    },
    '&.notistack-MuiContent-warning': {
      backgroundColor: theme.palette.warning.main,
    },
    '&.notistack-MuiContent-info': {
      backgroundColor: theme.palette.info.main,
    },
  })
);

const NotFoundOrRedirect = () => {
  const { isAuthenticate } = useSelector((state) => state.users);
  const location = useLocation();
  const realmId = localStorage.getItem('REALM_ID');
  const accountId = localStorage.getItem('accountId');

  if (!isAuthenticate) {
    if (!realmId || !accountId) {
      return <Navigate to="/account" state={{ from: location }} replace />;
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <NotFound404 />;
};

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff: 1s, 2s, 4s... capped at 30s
    staleTime: 5 * 60 * 1000, // 5 minutes
  },
});

function App() {
  const { isAuthenticate } = useSelector((state) => state.users);
  const theme = useCurrentTheme();
  const notistackRef = useRef();
  const { users } = useSelector((state) => state);
  const { permissions } = users;
  const { setCurrentSavedTheme } = useThemeAppearance();
  const { validateAccount } = useAccountId();
  const dispatch = useDispatch();

  // Common Auth Hook
  const { initializeCommonAuth, isCommonAuthEnabled: commonAuthEnabled } =
    useCommonAuth();

  const [loading, setLoading] = useState(true);

  // Explicit logout ends the shared session. Other tabs clear their own
  // sessionStorage and reload so Redux authentication state is reconstructed
  // from the now-cleared localStorage instead of remaining stale until an API 401.
  useEffect(
    () =>
      subscribeToSessionLogout(() => {
        sessionStorage.clear();
        window.location.reload();
      }),
    []
  );

  // A completed login or identity change in another tab updates shared tokens
  // but not this tab's in-memory Redux state. Reload after clearing tab-local
  // state; public auth routes go to the protected root to avoid common-auth
  // guards redirecting an already authenticated session back to the provider.
  useEffect(
    () =>
      subscribeToSessionLogin(() => {
        sessionStorage.clear();
        const publicAuthPaths = [
          '/login',
          '/account',
          '/org',
          '/clear-client-storage',
        ];
        const isPublicAuthPath = publicAuthPaths.some(
          (path) =>
            window.location.pathname === path ||
            window.location.pathname.startsWith(`${path}/`)
        );

        if (isPublicAuthPath) {
          window.location.replace('/');
        } else {
          window.location.reload();
        }
      }),
    []
  );

  // Initialize Common Auth if enabled and URL contains tokens
  useLayoutEffect(() => {
    if (commonAuthEnabled) {
      // Create URLSearchParams inside useEffect to avoid dependency issues
      const urlParams = new URLSearchParams(window.location.search);

      const extractedUrlParams = extractAuthParams(urlParams);

      // Check if we have URL parameters for common auth using centralized validation
      const hasAuthParams = validateCommonAuthParams(extractedUrlParams);

      if (hasAuthParams) {
        initializeCommonAuth()
          .then((success) => {
            if (success) {
              // Add a small delay to ensure Redux store is updated
              setTimeout(() => {
                setLoading(false);
              }, 500); // 500ms delay to ensure Redux store update
            } else {
              // Common auth failed, clear any partial auth data and redirect to error handling
              clearCommonAuthData();
              setLoading(false);
            }
          })
          .catch((__error) => {
            setLoading(false);
          });
      } else {
        // No common auth URL params but might have stored tokens, proceed with normal loading
        setLoading(false);
      }
    } else {
      // Common auth not enabled, proceed with normal loading
      setLoading(false);
    }
  }, [commonAuthEnabled, initializeCommonAuth]); // Remove urlParams from dependency array

  // Define the handler function
  const keycloakLoginSuccessHandler = (event) => {
    const { auth, keycloakConfig } = event.detail;
    if (!auth || !keycloakConfig) return;

    const keycloakUserData = keycloakConfig.tokenParsed;
    const userData = {
      username: keycloakUserData.preferred_username,
      email: keycloakUserData.email,
      id: keycloakUserData.sub,
    };

    window.KEYCLOAK_CONFIG = keycloakConfig;
    dispatch(
      loginSuccess({
        isAuthenticate: auth,
        token: keycloakConfig.token,
        refresh: keycloakConfig.refreshToken,
        user: userData,
      })
    );
    setLoading(false);

    if (auth) {
      if (window.location?.pathname === '/account') {
        window.location.assign('/');
      }
    }
  };

  // Register a global Keycloak event handler
  useEffect(() => {
    document.addEventListener(
      'KEYCLOAK_LOGIN_SUCCESS',
      keycloakLoginSuccessHandler
    );

    if (!REALM_CONFIG?.launcher) {
      return () => {
        document.removeEventListener(
          'KEYCLOAK_LOGIN_SUCCESS',
          keycloakLoginSuccessHandler
        );
      };
    }
    return () => {};
  }, [dispatch, loginSuccess]);

  // for umami user identification
  useEffect(() => {
    if (!isAuthenticate) return;
    identifyLoggedInUserWithUmami(users?.userData, users?.profileData);
  }, [isAuthenticate, users?.userData?.id, users?.profileData?.role]);

  const validateAccountId = (id) => {
    validateAccount({ account_id: id }).then((res) => {
      if (res) {
        setCurrentSavedTheme(res?.data?.theme);
        localStorage.setItem('authMode', res?.data?.auth?.mode);
        localStorage.setItem('loginEndpoint', res?.data?.auth?.endpoint);
      }
    });
  };

  useEffect(() => {
    if (window.location.href?.includes('login') && !isAuthenticate) {
      // to get accountId form url
      const splitUrlArray = window.location.href.split('/');
      const accountIdWithOtherKeys = splitUrlArray[splitUrlArray.length - 1];
      const accountId = accountIdWithOtherKeys?.split('?')[0];
      if (
        !accountId?.includes('login') &&
        accountId &&
        REGEX.USERNAME.test(accountId)
      ) {
        validateAccountId(accountId);
        localStorage.setItem('accountId', accountId);
      }
    }
  }, [isAuthenticate]);

  useEffect(() => {
    // @ts-ignore
    window.enqueueSnackbar = notistackRef.current.enqueueSnackbar;
    window.closeSnackbar = notistackRef.current.closeSnackbar;
  }, []);

  /**
   * @function  onClickSnackBarClose
   * @description when click on cross button of toast dialog it will close the snack bar
   */
  const onClickSnackBarClose = useCallback((key) => {
    notistackRef.current.closeSnackbar(key);
  }, []);

  const router = React.useMemo(() => {
    // Routes that should be excluded from CommonAuthGuard protection
    const excludedPaths = ['/clear-client-storage', '*'];

    // Wrap public routes with CommonAuthGuard when common auth is enabled
    // Exclude certain utility routes that should always be accessible
    const protectedPublicRoutes = publicRoutes.map((route) => {
      if (route.path === '*') {
        return {
          ...route,
          element: <NotFoundOrRedirect />,
        };
      }
      const shouldProtect = !excludedPaths.includes(route.path);

      return {
        ...route,
        element: shouldProtect ? (
          <CommonAuthGuard>{route.element}</CommonAuthGuard>
        ) : (
          route.element
        ),
      };
    });

    return createBrowserRouter([
      ...protectedPublicRoutes,
      {
        path: '/',
        element: <PrivateRoute loading={loading} />,
        children: getProtectedRoutes(permissions),
        errorElement: <ErrorBoundary />,
      },
    ]);
  }, [permissions, loading]);

  const renderActionComponent = useCallback(
    (key) => (
      <CloseSnackbarIconButton
        key={key}
        onClickSnackBarClose={onClickSnackBarClose}
      />
    ),
    [onClickSnackBarClose]
  );

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider
          ref={notistackRef}
          maxSnack={5}
          hideIconVariant
          preventDuplicate
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          autoHideDuration={5000}
          action={renderActionComponent}
          Components={{
            report: AutomationReportToast,
            weaknessAssessment: WeaknessAssessmentToast,
            aiAutofill: AIAutofillProcessingToast,
            examinationReview: ExaminationReviewToast,
            success: StyledMaterialDesignContent,
            error: StyledMaterialDesignContent,
            warning: StyledMaterialDesignContent,
            info: StyledMaterialDesignContent,
          }}
        >
          <QueryClientProvider client={queryClient}>
            <FullscreenLoader />
            <SessionExpiredModal />
            <SuspenseComponent>
              <RouterProvider router={router} />
            </SuspenseComponent>

            {
              // eslint-disable-next-line no-undef
              process.env.NODE_ENV === 'development' && (
                <ReactQueryDevtools
                  initialIsOpen={false}
                  buttonPosition="bottom-left"
                />
              )
            }
          </QueryClientProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default App;
